/**
 * AI Performance Monitoring Utilities
 * Provides functions for tracking and analyzing AI feature performance
 */
/**
 * Performance monitoring class for AI features
 */
export class AIPerformanceMonitor {
    constructor(maxEvents = 1000) {
        this.events = [];
        this.benchmarks = [];
        this.maxEvents = 1000;
        this.maxEvents = maxEvents;
        this.initializeBenchmarks();
    }
    /**
     * Initialize default performance benchmarks
     */
    initializeBenchmarks() {
        this.benchmarks = [
            {
                feature: 'recommendations',
                metric: 'latency',
                target: 500,
                warning: 1000,
                critical: 2000,
                unit: 'ms',
            },
            {
                feature: 'mastery_calculation',
                metric: 'latency',
                target: 200,
                warning: 500,
                critical: 1000,
                unit: 'ms',
            },
            {
                feature: 'adaptation',
                metric: 'latency',
                target: 300,
                warning: 800,
                critical: 1500,
                unit: 'ms',
            },
            {
                feature: 'cache',
                metric: 'hit_rate',
                target: 0.8,
                warning: 0.6,
                critical: 0.4,
                unit: 'ratio',
            },
            {
                feature: 'ai_features',
                metric: 'error_rate',
                target: 0.01,
                warning: 0.05,
                critical: 0.1,
                unit: 'ratio',
            },
        ];
    }
    /**
     * Start tracking a performance event
     */
    startEvent(feature, action, metadata) {
        const id = `${feature}_${action}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const event = {
            id,
            feature,
            action,
            startTime: performance.now(),
            success: false,
            metadata: metadata || {},
            userId: metadata?.['userId'],
            sessionId: metadata?.['sessionId'],
        };
        this.events.push(event);
        this.cleanupOldEvents();
        return id;
    }
    /**
     * End tracking a performance event
     */
    endEvent(eventId, success = true, error, additionalMetadata) {
        const event = this.events.find(e => e.id === eventId);
        if (!event) {
            console.warn(`Performance event ${eventId} not found`);
            return;
        }
        event.endTime = performance.now();
        event.duration = event.endTime - event.startTime;
        event.success = success;
        if (error) {
            event.error = error;
        }
        if (additionalMetadata) {
            event.metadata = { ...event.metadata, ...additionalMetadata };
        }
        // Check for performance alerts
        this.checkPerformanceAlerts(event);
    }
    /**
     * Record a simple performance metric
     */
    recordMetric(feature, metric, value, metadata) {
        const eventId = this.startEvent(feature, `record_${metric}`, metadata);
        this.endEvent(eventId, true, undefined, { [metric]: value });
    }
    /**
     * Get performance metrics for a specific period
     */
    getMetrics(feature, periodMinutes = 60) {
        const cutoff = Date.now() - periodMinutes * 60 * 1000;
        const relevantEvents = this.events.filter(event => event.startTime >= cutoff &&
            (!feature || event.feature === feature) &&
            event.endTime !== undefined);
        const failedEvents = relevantEvents.filter(e => !e.success);
        // Calculate latency metrics
        const recommendationEvents = relevantEvents.filter(e => e.feature === 'recommendations' && e.duration !== undefined);
        const masteryEvents = relevantEvents.filter(e => e.feature === 'mastery_calculation' && e.duration !== undefined);
        const adaptationEvents = relevantEvents.filter(e => e.feature === 'adaptation' && e.duration !== undefined);
        // Calculate cache metrics
        const cacheEvents = relevantEvents.filter(e => e.action.includes('cache'));
        const cacheHits = cacheEvents.filter(e => e.metadata?.['hit'] === true);
        return {
            recommendationLatency: this.calculateAverageLatency(recommendationEvents),
            masteryCalculationTime: this.calculateAverageLatency(masteryEvents),
            adaptationResponseTime: this.calculateAverageLatency(adaptationEvents),
            cacheHitRate: cacheEvents.length > 0 ? cacheHits.length / cacheEvents.length : 0,
            offlineCapability: this.calculateOfflineCapability(relevantEvents),
            errorRate: relevantEvents.length > 0
                ? failedEvents.length / relevantEvents.length
                : 0,
            userSatisfaction: this.calculateUserSatisfaction(relevantEvents),
        };
    }
    /**
     * Generate performance report
     */
    generateReport(periodMinutes = 60) {
        const metrics = this.getMetrics(undefined, periodMinutes);
        const alerts = this.getActiveAlerts();
        const trends = this.calculateTrends(periodMinutes);
        return {
            period: `${periodMinutes} minutes`,
            metrics,
            benchmarks: this.benchmarks,
            alerts,
            trends,
        };
    }
    /**
     * Check for performance alerts
     */
    checkPerformanceAlerts(event) {
        if (!event.duration)
            return;
        const benchmark = this.benchmarks.find(b => b.feature === event.feature && b.metric === 'latency');
        if (!benchmark)
            return;
        let alertLevel = null;
        if (event.duration >= benchmark.critical) {
            alertLevel = 'critical';
        }
        else if (event.duration >= benchmark.warning) {
            alertLevel = 'warning';
        }
        else if (event.duration <= benchmark.target) {
            alertLevel = 'info';
        }
        if (alertLevel && alertLevel !== 'info') {
            console.warn(`Performance alert: ${event.feature} ${event.action} took ${event.duration}ms (${alertLevel})`);
        }
    }
    /**
     * Calculate average latency for events
     */
    calculateAverageLatency(events) {
        if (events.length === 0)
            return 0;
        const totalDuration = events.reduce((sum, event) => sum + (event.duration || 0), 0);
        return totalDuration / events.length;
    }
    /**
     * Calculate offline capability metric
     */
    calculateOfflineCapability(events) {
        const offlineEvents = events.filter(e => e.metadata?.['offline'] === true);
        const successfulOfflineEvents = offlineEvents.filter(e => e.success);
        return offlineEvents.length > 0
            ? successfulOfflineEvents.length / offlineEvents.length
            : 1;
    }
    /**
     * Calculate user satisfaction metric
     */
    calculateUserSatisfaction(events) {
        const satisfactionEvents = events.filter(e => e.metadata?.['userSatisfaction'] !== undefined);
        if (satisfactionEvents.length === 0)
            return 0.8; // Default assumption
        const totalSatisfaction = satisfactionEvents.reduce((sum, event) => sum + (event.metadata?.['userSatisfaction'] || 0), 0);
        return totalSatisfaction / satisfactionEvents.length;
    }
    /**
     * Get active performance alerts
     */
    getActiveAlerts() {
        // This would typically query a persistent store
        // For now, return empty array as alerts are logged in real-time
        return [];
    }
    /**
     * Calculate performance trends
     */
    calculateTrends(periodMinutes) {
        const trends = [];
        const features = [...new Set(this.events.map(e => e.feature))];
        features.forEach(feature => {
            const currentMetrics = this.getMetrics(feature, periodMinutes);
            const previousMetrics = this.getMetrics(feature, periodMinutes * 2);
            // Compare latency trends
            const latencyChange = currentMetrics.recommendationLatency -
                previousMetrics.recommendationLatency;
            trends.push({
                feature,
                metric: 'latency',
                direction: latencyChange > 10
                    ? 'degrading'
                    : latencyChange < -10
                        ? 'improving'
                        : 'stable',
                change: latencyChange,
                period: `${periodMinutes}m`,
            });
        });
        return trends;
    }
    /**
     * Clean up old events to prevent memory leaks
     */
    cleanupOldEvents() {
        if (this.events.length > this.maxEvents) {
            this.events = this.events.slice(-this.maxEvents);
        }
    }
    /**
     * Export performance data
     */
    exportData() {
        return {
            events: [...this.events],
            benchmarks: [...this.benchmarks],
            timestamp: Date.now(),
        };
    }
    /**
     * Clear all performance data
     */
    clear() {
        this.events = [];
    }
}
/**
 * Global performance monitor instance
 */
export const aiPerformanceMonitor = new AIPerformanceMonitor();
/**
 * Decorator for automatic performance tracking
 */
export function trackPerformance(feature, action) {
    return function (_target, propertyName, descriptor) {
        const method = descriptor.value;
        descriptor.value = async function (...args) {
            const actionName = action || propertyName;
            const eventId = aiPerformanceMonitor.startEvent(feature, actionName);
            try {
                const result = await method.apply(this, args);
                aiPerformanceMonitor.endEvent(eventId, true);
                return result;
            }
            catch (error) {
                aiPerformanceMonitor.endEvent(eventId, false, error instanceof Error ? error.message : 'Unknown error');
                throw error;
            }
        };
    };
}
/**
 * Utility function for manual performance tracking
 */
export async function withPerformanceTracking(feature, action, fn, metadata) {
    const eventId = aiPerformanceMonitor.startEvent(feature, action, metadata);
    try {
        const result = await fn();
        aiPerformanceMonitor.endEvent(eventId, true);
        return result;
    }
    catch (error) {
        aiPerformanceMonitor.endEvent(eventId, false, error instanceof Error ? error.message : 'Unknown error');
        throw error;
    }
}
